// `(spread ds :key :col :value :prefix :limit N)` — array column → N
// indexed columns. Top-level eager op; composes via threading.

import { z } from 'zod';

import { nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type SpecialCtx } from '../../op-meta.ts';
import { kwargPath } from '../../path.ts';
import { materializedToRows } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnName, ColumnNameSchema, columnNameString, KeywordSchema } from '../../schema/keyword.ts';
import { type ColumnType, schemaFromEntries, unknownCol } from '../../schema/types.ts';
import { type Cell, MaterializedDataset, type Value } from '../../value.ts';
import {
  columnNameAstName,
  DEFAULT_RESHAPE_LIMIT,
  LimitSchema,
  requireColumns,
  resolveDsArg,
  rowsToColumns,
} from './helpers.ts';

/**
 * `(spread ds :key :col :value :prefix :limit N)` — explode an array
 * column into N indexed columns. Each element of the source array
 * becomes a new column named `<prefix>-<i>` (0-based, up to `:limit`,
 * default `DEFAULT_RESHAPE_LIMIT`). Rows whose array has fewer
 * elements get `null` in the trailing columns.
 *
 *   - `:key` (keyword | string, required) — the source array column.
 *   - `:value` (keyword, required) — the prefix for output columns.
 *     Symmetric with `fold`'s `:value-name`.
 *   - `:limit` (int, optional) — caps the fan-out width.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - `:key` isn't a column on `ds.schema` → `UnknownColumnRefError`
 *     (with the dataset's columns as the "did you mean").
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * reshape runs.
 */
export const spread = z
  .object({
    name: z.literal('spread'),
    params: z.tuple([astArg('<dataset>')]),
    key: ColumnNameSchema,
    value: KeywordSchema,
    limit: LimitSchema,
  })
  .describe(
    'Array column → N indexed columns. `:key` is the source array column; `:value` is the output prefix; ' +
      '`:limit` caps the fan-out width.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(-> wells (spread :key :tags :value :tag :limit 5))' },
    apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
      const [dsRaw] = data.params as readonly [Value];
      const concrete = await resolveDsArg(dsRaw, '(spread ds :key :col :value :prefix)', ctx);
      const sourceCol = columnNameString(data.key as ColumnName);
      const prefix = (data.value as { name: string }).name;
      const limit = data.limit ?? DEFAULT_RESHAPE_LIMIT;

      requireColumns([sourceCol], concrete.schema, () => nodePos(ctx.spans, kwargPath(ctx.path, 'key')));

      const rows = materializedToRows(concrete);
      // Detect actual width — min of any row's array length and :limit.
      const width = rows.reduce<number>((max, row) => {
        const v = row[sourceCol];
        return Array.isArray(v) ? Math.max(max, Math.min(v.length, limit)) : max;
      }, 0);

      const idCols = Array.from(concrete.schema.keys()).filter((c) => c !== sourceCol);
      const spreadCols = Array.from({ length: width }, (_, i) => `${prefix}-${i}`);

      const outCols: Record<string, Cell[]> = {
        ...rowsToColumns(rows, idCols),
        ...Object.fromEntries(
          spreadCols.map((sc, i) => [
            sc,
            rows.map((r) => {
              const v = r[sourceCol];
              return Array.isArray(v) ? ((v[i] ?? null) as Cell) : null;
            }),
          ]),
        ),
      };

      const entries: (readonly [string, ColumnType])[] = [
        ...idCols.map((c) => [c, concrete.schema.get(c) ?? unknownCol] as const),
        ...spreadCols.map((sc) => [sc, unknownCol] as const),
      ];
      // 1:1 — one output row per input row (the array fans out into
      // columns, not rows), so the identity axis passes through verbatim.
      return new MaterializedDataset(outCols, schemaFromEntries(entries), concrete.identity);
    },
    dataset: {
      // Runtime-width: the fan-out depends on the longest array in
      // `:key` (capped by `:limit`). No static schema rule — declaring
      // `dataset` engages the engine's three-branch wrapper, and
      // returning `undefined` triggers force-materialise so apply
      // computes the precise schema on concrete data.
      propagateSchema: () => undefined,
      // The `:key` kwarg is the only column USAGE — `:value` is the
      // output prefix (definition), excluded.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const keyAst = (data as { readonly key?: unknown }).key;
        const name = columnNameAstName(keyAst);
        return name === undefined ? [] : [{ name, path: kwargPath(basePath, 'key') }];
      },
    },
  });
